import {
    ClientIntakeMessage,
    CommunityRelationshipType,
    EvergreenCommunityListingAvailability,
    FileEntityId,
    globalFilesEntityId,
    InternalId,
    IPlacement,
} from "@sp-crm/core";
import React, { useCallback } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { sendClientIntakeEmail } from "store/actions/email";
import { loadFilesAction } from "store/actions/files";
import { useFeature, usePreferences, useUserContacts } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { ApplicationState } from "store/state";
import { handleEmailSent } from "util/email-sent";
import {
    useComposeMessageQuery,
    useGetClientForIntakeEmailQuery,
} from "../../../../generated/graphql";
import { ClientIntakeEmailDialogInternal } from "./email-dialog";

const staleTime = 1000 * 60 * 10; // 10 minutes

interface ClientIntakeEmailDialogToClientProps {
    placement: IPlacement;
    onDismiss: () => void;
    includeDisclosure: boolean;
}

export const ClientIntakeEmailDialogToClient = (
    props: ClientIntakeEmailDialogToClientProps,
) => {
    const query = useComposeMessageQuery(
        {
            entityId: props.placement.id,
            specialMessageExperience: "",
        },
        {
            staleTime,
            cacheTime: 0,
        },
    );
    const queryClient = useQueryClient();
    const requests = useSelector((state: ApplicationState) => state.status.events);
    const disclosureEnabled = useFeature("clientDisclosures");
    const { settings } = useTenantSettings();
    const preferences = usePreferences();
    const dispatch = useDispatch();
    const loadFiles = useCallback(
        (entityId: FileEntityId) => {
            loadFilesAction(entityId, dispatch);
        },
        [dispatch],
    );
    const send = useCallback(
        (eventId: InternalId, data: ClientIntakeMessage) =>
            sendClientIntakeEmail(eventId, data, dispatch).then(() =>
                handleEmailSent(queryClient),
            ),
        [dispatch, queryClient],
    );
    const files = useSelector((state: ApplicationState) => {
        return [
            {
                entityName: "Our Files",
                files: (state.files.files[globalFilesEntityId] || []).filter(
                    x => x.shareWithClient,
                ),
            },
            {
                entityName: props.placement.preferredClientName,
                files: (state.files.files[props.placement.id] || []).filter(
                    x => x.shareWithClient,
                ),
            },
        ];
    });

    const userContacts = useUserContacts();

    if (props.placement && query.isSuccess && query.data && query.data.composeMessage) {
        return (
            <ClientIntakeEmailDialogInternal
                clientOnly={true}
                disclosureEnabled={disclosureEnabled}
                clientDisclosureFileId={settings.clientDisclosureFileId}
                recipients={query.data.composeMessage.recipients}
                userPreferences={preferences}
                bulkEmailEnabled={false}
                inClientIntakeWorkflow={false}
                mode="intake"
                files={files}
                loadFiles={loadFiles}
                send={send}
                placement={props.placement}
                clientCommunities={[]}
                requests={requests}
                initialTemplateId={
                    props.includeDisclosure && disclosureEnabled
                        ? settings.clientDisclosureSendTemplateId
                        : null
                }
                clientId={props.placement.id}
                includeDisclosure={props.includeDisclosure}
                onDismiss={props.onDismiss}
                userContacts={userContacts}
                includeCommunityAvailability={false}
            />
        );
    }

    return <></>;
};

interface ClientIntakeEmailDialogToCommunityProps {
    placement: IPlacement;
    onDismiss: () => void;
}

export const ClientIntakeEmailDialogToCommunity = (
    props: ClientIntakeEmailDialogToCommunityProps,
) => {
    const clientIntakeQuery = useGetClientForIntakeEmailQuery(
        {
            clientId: props.placement.id,
        },
        {
            cacheTime: 0,
        },
    );
    const queryClient = useQueryClient();
    const requests = useSelector((state: ApplicationState) => state.status.events);
    const { settings } = useTenantSettings();
    const preferences = usePreferences();
    const dispatch = useDispatch();
    const loadFiles = useCallback(
        (entityId: FileEntityId) => {
            loadFilesAction(entityId, dispatch);
        },
        [dispatch],
    );
    const send = useCallback(
        (eventId: InternalId, data: ClientIntakeMessage) =>
            sendClientIntakeEmail(eventId, data, dispatch).then(() =>
                handleEmailSent(queryClient),
            ),
        [dispatch, queryClient],
    );
    const placement = props.placement;

    const files = useSelector((state: ApplicationState) => {
        return [
            {
                entityName: "Our Files",
                files: (state.files.files[globalFilesEntityId] || []).filter(
                    x => x.shareWithClient,
                ),
            },
            {
                entityName: placement.preferredClientName,
                files: (state.files.files[placement.id] || []).filter(
                    x => x.shareWithClient,
                ),
            },
        ];
    });

    const userContacts = useUserContacts();

    if (
        placement &&
        clientIntakeQuery.isSuccess &&
        clientIntakeQuery.data &&
        clientIntakeQuery.data.getClient
    ) {
        const atLeastOneCommunityHasUnknownAvailability = (
            clientIntakeQuery.data.getClient.clientCommunities || []
        ).some(
            cc =>
                cc.relationship !== CommunityRelationshipType.excluded &&
                cc.community.isConnectedToEvergreen &&
                (!cc.community.availability ||
                    cc.community.availability.status ===
                        EvergreenCommunityListingAvailability.Unknown),
        );
        const includeCommunityAvailability = atLeastOneCommunityHasUnknownAvailability;

        return (
            <ClientIntakeEmailDialogInternal
                clientCommunities={clientIntakeQuery.data.getClient.clientCommunities}
                clientOnly={false}
                disclosureEnabled={false}
                clientDisclosureFileId={settings.clientDisclosureFileId}
                userPreferences={preferences}
                bulkEmailEnabled={preferences.useBulkCommunityEmail}
                inClientIntakeWorkflow={true}
                mode="intake"
                includeDisclosure={false}
                files={files}
                loadFiles={loadFiles}
                send={send}
                requests={requests}
                placement={placement}
                clientId={placement.id}
                onDismiss={props.onDismiss}
                userContacts={userContacts}
                includeCommunityAvailability={includeCommunityAvailability}
            />
        );
    }

    return <></>;
};

interface NotifyUnselectedCommunitiesDialogProps {
    placement: IPlacement;
    onDismiss: () => void;
}

export const NotifyUnselectedCommunitiesDialog = (
    props: NotifyUnselectedCommunitiesDialogProps,
) => {
    const clientIntakeQuery = useGetClientForIntakeEmailQuery(
        {
            clientId: props.placement.id,
        },
        {
            cacheTime: 0,
        },
    );
    const queryClient = useQueryClient();
    const requests = useSelector((state: ApplicationState) => state.status.events);
    const preferences = usePreferences();
    const dispatch = useDispatch();
    const loadFiles = useCallback(
        (entityId: FileEntityId) => {
            loadFilesAction(entityId, dispatch);
        },
        [dispatch],
    );
    const send = useCallback(
        (eventId: InternalId, data: ClientIntakeMessage) =>
            sendClientIntakeEmail(eventId, data, dispatch).then(() =>
                handleEmailSent(queryClient),
            ),
        [dispatch, queryClient],
    );
    const placement = props.placement;

    const files = useSelector((state: ApplicationState) => {
        return [
            {
                entityName: "Our Files",
                files: (state.files.files[globalFilesEntityId] || []).filter(
                    x => x.shareWithClient,
                ),
            },
            {
                entityName: placement.preferredClientName,
                files: (state.files.files[placement.id] || []).filter(
                    x => x.shareWithClient,
                ),
            },
        ];
    });

    const userContacts = useUserContacts();

    if (
        placement &&
        clientIntakeQuery.isSuccess &&
        clientIntakeQuery.data &&
        clientIntakeQuery.data.getClient
    ) {
        return (
            <ClientIntakeEmailDialogInternal
                clientCommunities={clientIntakeQuery.data.getClient.clientCommunities}
                clientOnly={false}
                disclosureEnabled={false}
                clientDisclosureFileId={null}
                userPreferences={preferences}
                bulkEmailEnabled={true}
                inClientIntakeWorkflow={false}
                includeDisclosure={false}
                files={files}
                loadFiles={loadFiles}
                send={send}
                requests={requests}
                placement={placement}
                clientId={placement.id}
                mode="notify"
                onDismiss={props.onDismiss}
                userContacts={userContacts}
                includeCommunityAvailability={false}
            />
        );
    }

    return <></>;
};
